import React, { useState } from 'react';
import { CustomButton } from '@/components/atomic';
import { useToastContext } from '@/components/ui/ToastProvider';
import PhoneInput from '@/components/atomic/phone-input';
import { validatePhoneByAreaCode } from '@/components/atomic/phone-validation';
import { forgotPasswordBySMS } from '@/lib/api/user';

interface GetSMSModalProps {
  onBack?: () => void;
  onSuccess?: (phoneNumber: string) => void;
}

const GetSMSModal: React.FC<GetSMSModalProps> = ({ onBack, onSuccess }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [rawPhone, setRawPhone] = useState('');
  const { toast } = useToastContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handlePhoneChange = (value: string) => {
    setRawPhone(value);
    setPhoneNumber(value);
    setError(''); // Clear error on change
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneNumber) {
      setError('Enter your phone number');
      return;
    }
    
    // Extract country code from rawPhone for country-specific validation
    const countryCode = rawPhone.split('-')[0];
    const phoneValidation = validatePhoneByAreaCode(rawPhone, countryCode);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.message || 'Enter a valid phone number');
      return;
    }
    setLoading(true);
    try {
      const response = await forgotPasswordBySMS(phoneNumber);
      toast.success(response.message || 'SMS sent successfully!');
      if (onSuccess) {
        onSuccess(phoneNumber);
      }
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      if (Array.isArray(apiMessage)) {
        apiMessage.forEach((msg: string) => toast.error(msg || 'Something went wrong. Please try again.'));
      } else if (typeof apiMessage === 'string') {
        toast.error(apiMessage);
      } else {
        toast.error('Failed to send SMS.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Header with back arrow */}
      <div className="flex items-center mb-6">
        {onBack && (
          <button 
            onClick={onBack}
            className="mr-3 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        <h2 className="font-rakesly text-xl font-bold text-gray-800 tracking-wide">
          FORGOT PASSWORD?
        </h2>
      </div>

      {/* Description */}
      <p className="text-gray-600 font-sans text-sm mb-6 leading-relaxed">
        Please enter the number used to create your account
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <PhoneInput
          value={phoneNumber}
          onChange={handlePhoneChange}
          error={error}
        />

        <p className="text-gray-500 font-sans text-xs">
          We'll send you an SMS with your email attached to the number
        </p>

        <div className="pt-4">
          <CustomButton 
            type="submit"
            className="w-full bg-gray-800 text-white py-2 px-4 rounded-lg font-sans font-medium hover:bg-gray-700 transition-all duration-200"
            disabled={loading}
          >
            {loading ? 'Requesting...' : 'Request Password Reset '}
          </CustomButton>
        </div>
      </form>
    </div>
  );
};

export default GetSMSModal;